import { useState } from 'react';
import { cn } from '@/lib/utils';
import { useAttributionSourcesQuery } from '@/client/api/marketing/attribution';
import { EmptyState } from './components/marketing-ui';

const RANGES = [7, 30, 90] as const;
/** 24h visits at this multiple of the prior daily average is worth a look. */
const SPIKE = 3;

function Tile({ label, value }: { label: string; value: number | undefined }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div className="text-2xl font-semibold tabular-nums text-gray-900">{value ?? '—'}</div>
    </div>
  );
}

/**
 * Where visitors come from, measured in their browser (ff-api
 * service/acquisition.py). "Ours" rows carry our campaign tags; everything else
 * arrived without us asking — and an untagged spike is someone posting about us.
 */
export default function TrafficSourcesPage() {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, error } = useAttributionSourcesQuery(days);
  const sources = data?.sources ?? [];
  const spikes = sources.filter((s) => !s.sanctioned && (s.spike_ratio ?? 0) >= SPIKE);

  return (
    <div className="space-y-4">
      <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
        {RANGES.map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setDays(d)}
            className={cn(
              'rounded-md px-3 py-1 text-sm',
              days === d ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50',
            )}
          >
            {d}d
          </button>
        ))}
      </div>

      {error && <p className="text-sm text-red-600">Could not load traffic sources.</p>}

      <div className="grid gap-3 sm:grid-cols-3">
        <Tile label="Visits" value={data?.visits} />
        <Tile label="Signups" value={data?.signups} />
        <Tile label="Sources" value={sources.length} />
      </div>

      {spikes.length > 0 && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <span className="font-medium">Untagged spike:</span>{' '}
          {spikes
            .map((s) => `${s.source} (${s.visits_24h} in 24h, ${s.spike_ratio}× usual)`)
            .join(' · ')}
          . Someone may be posting about us — check Radar.
        </div>
      )}

      {!isLoading && sources.length === 0 ? (
        <EmptyState label="No visits recorded in this window yet." />
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Channel</th>
                <th className="px-3 py-2 font-medium">Ours?</th>
                <th className="px-3 py-2 text-right font-medium">Visits</th>
                <th className="px-3 py-2 text-right font-medium">Last 24h</th>
                <th className="px-3 py-2 text-right font-medium">vs usual</th>
                <th className="px-3 py-2 text-right font-medium">Signups</th>
              </tr>
            </thead>
            <tbody>
              {sources.map((s) => (
                <tr key={`${s.channel}:${s.source}:${s.sanctioned}`} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-gray-900">{s.source}</td>
                  <td className="px-3 py-2 text-gray-600">{s.channel}</td>
                  <td className="px-3 py-2 text-gray-600">{s.sanctioned ? 'Campaign' : 'Organic'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.visits}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.visits_24h}</td>
                  <td
                    className={cn(
                      'px-3 py-2 text-right tabular-nums',
                      (s.spike_ratio ?? 0) >= SPIKE && 'font-semibold text-amber-700',
                    )}
                  >
                    {s.spike_ratio == null ? 'new' : `${s.spike_ratio}×`}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{s.signups}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(data?.campaigns?.length ?? 0) > 0 && (
        <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-3 py-2 text-sm font-medium text-gray-900">
            Tagged campaigns
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-xs text-gray-500">
                <th className="px-3 py-2 font-medium">Campaign</th>
                <th className="px-3 py-2 font-medium">Source</th>
                <th className="px-3 py-2 font-medium">Link code</th>
                <th className="px-3 py-2 text-right font-medium">Visits</th>
                <th className="px-3 py-2 text-right font-medium">Signups</th>
              </tr>
            </thead>
            <tbody>
              {data!.campaigns.map((c, i) => (
                <tr key={`${c.utm_id}:${c.utm_campaign}:${c.source}:${i}`} className="border-b border-gray-100">
                  <td className="px-3 py-2 text-gray-900">{c.utm_campaign ?? '—'}</td>
                  <td className="px-3 py-2 text-gray-600">{c.source}</td>
                  <td className="px-3 py-2 font-mono text-xs text-gray-600">{c.utm_id ?? '—'}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.visits}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{c.signups}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data?.note && <p className="text-xs text-gray-500">{data.note}</p>}
    </div>
  );
}
